/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
*                _
*  _            | | _
* | |         _ | || |
* | |__   _  | || || |__
* | '_ \ | | | || || '_ \
* | (_) || |_| || || (_) |
*  \___/  \___/ |_| \___/
*
*
*/

/**
 * @module image/image/converters
 */

import { first } from 'ckeditor5';

/**
 * Returns a function that converts the image view representation:
 *
 *    <figure class="image"><img src="..." alt="..."></img></figure>
 *
 * to the model representation:
 *
 *    <image src="..." alt="..."></image>
 *
 * The entire content of the `<figure>` element except the first `<img>` is being converted as children
 * of the `<image>` model element.
 *
 * @returns {Function}
 */
export function viewFigureToModel() {

  function converter(evt, data, conversionApi) {
    // Do not convert if this is not an "image figure".
    if (!conversionApi.consumable.test(data.viewItem, { name: true, classes: 'image' })) {
      return;
    }

    // Find an image element inside the figure element.
    // @ts-ignore
    const viewImage = Array.from(data.viewItem.getChildren()).find((viewChild) => viewChild.is('img'));

    // Do not convert if image element is absent, is missing src attribute or was already converted.
    // @ts-ignore
    if (!viewImage || !viewImage.hasAttribute('src') || !conversionApi.consumable.test(viewImage, { name: true })) {
      return;
    }

    // Convert view image to model image.
    const conversionResult = conversionApi.convertItem(viewImage, data.modelCursor);

    // Get image element from conversion result.
    const modelImage = first(conversionResult.modelRange.getItems());

    // When image wasn't successfully converted then finish conversion.
    if (!modelImage) {
      return;
    }

    // Convert rest of the figure element's children as an image children.
    conversionApi.convertChildren(data.viewItem, conversionApi.writer.createPositionAt(modelImage, 0));

    // Set image range as conversion result.
    data.modelRange = conversionResult.modelRange;

    // Continue conversion where image conversion ends.
    data.modelCursor = conversionResult.modelCursor;
  }

  return (dispatcher) => {
    dispatcher.on('element:figure', converter);
  };
}

export function modelToViewAttributeConverter(attributeKey: string) {

  function converter(evt, data, conversionApi) {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }

    const viewWriter = conversionApi.writer;
    const figure = conversionApi.mapper.toViewElement(data.item);
    const img = figure.getChild(0);

    if (img) {
      if (data.attributeNewValue !== null) {
        viewWriter.setAttribute(data.attributeKey, data.attributeNewValue, img);
      } else {
        viewWriter.removeAttribute(data.attributeKey, img);
      }
    }
  }

  return (dispatcher) => {
    dispatcher.on(`attribute:${attributeKey}:image`, converter);
  };
}
